import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';

import {
    EzObject,
    EzBoolean,
    EzString,
    EzUrl
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState,
    EzParamName
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides operation utilities on the browser url
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzUrlHelper } from '/public/javascript/common/ezclocker-url-helper2.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Ready check:
 *     globalThis.ezApi.ezclocker?.[EzUrlHelper.ezApiName]?.ready;
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Ready event hook:
 *     EzClass.ezWaitReady(
 *         EzUrlHelper.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzUrlHelper extends EzClass {
    /**
     * @static
     * @public @readonly @property
     * Special 'hacker' id query param name for easter eggs :)
     */
    static get HACKER_ID_PARAM_NAME() {
        return 'hAk3ziD';
    }

    /**
     * @static
     * @public @readonly @property
     * Special 'hacker' id for easter eggs :)
     */
    get hackerId() {
        return EzUrl.readUrlParam('hAk3ziD');
    }

    /**
     * @private @field
     * Stores if debug mode is turned on (true) or off (false)
     * @type {boolean}
     */
    #ezDebugMode = false;
    /**
     * @public @property @getter
     * Gets if debug mode is turned on (true) or off (false)
     *
     * Always enabled for localhost and dev.ezclocker.com
     * @returns {boolean}
     */
    get ezDebugMode() {
        return this.#ezDebugMode ||
            'true' === EzUrl.readUrlParam('debug') ||
            'localhost' === window.location.hostname ||
            '127.0.0.1' === window.location.hostname ||
            'dev.ezclocker.com' === window.location.hostname;
    }
    /**
     * @public @property @setter
     * Sets if debug mode is turned on (true) or off (false)
     * @param {undefined|null|boolean} debugMode
     */
    set ezDebugMode(debugMode) {
        this.#ezDebugMode = EzBoolean.booleanOrFalse(
            debugMode ||
            'true' === EzUrl.readUrlParam('debug') ||
            'localhost' === window.location.hostname ||
            '127.0.0.1' === window.location.hostname ||
            'loc.ezclocker.com' === window.location.hostname ||
            'cto.ezclocker.com' === window.location.hostname ||
            'dev2.ezclocker.com' === window.location.hostname ||
            'dev.ezclocker.com' === window.location.hostname);
    }

    /**
     * @public @field
     * Stores the map of params on the current url
     * @type {object}
     */
    currentUrlParams = null;

    /**
     * @public @field
     * Stores the value for the 'email' param
     * @type {string}
     */
    emailParam = EzString.EMPTY;

    /**
     * @public @field
     * Stores the value for the 'v' param.
     * @type {string}
     */
    versionParam = EzString.EMPTY;

    /**
     * @protected @method
     * Initializes EzUrlHelper
     * @returns {EzUrlHelper}
     */
    ezInit() {
        EzUrlHelper.ezInstance.getUrlParams();

        return EzUrlHelper.ezInstance;
    }

    /**
     * @public @Method
     * Attempts to detect the enviornment from the browser url. Returns PRD by default.
     *
     * @returns {string}
     */
    ezGetEnvironmentFromUrl() {
        const url = window.location.href;

        if (url.startsWith('http://localhost') ||
            url.startsWith('https://localhost') ||
            url.startsWith('http://127.0.0.1') ||
            url.startsWith('https://127.0.0.1') ||
            url.startsWith('http://loc.ezclocker.com') ||
            url.startsWith('http://loc-app.ezclocker.com') ||
            url.startsWith('http://loc-api.ezclocker.com') ||
            url.startsWith('https://loc.ezclocker.com') ||
            url.startsWith('https://loc-app.ezclocker.com') ||
            url.startsWith('https://loc-api.ezclocker.com')) {
            return 'loc';
        }

        if (url.startsWith('https://dev.ezclocker.com') ||
            url.startsWith('https://dev-app.ezclocker.com') ||
            url.startsWith('https://dev-api.ezclocker.com') ||
            url.startsWith('https://dev1.ezclocker.com') ||
            url.startsWith('https://dev1-app.ezclocker.com') ||
            url.startsWith('https://dev1-api.ezclocker.com') ||
            url.startsWith('https://dev2.ezclocker.com') ||
            url.startsWith('https://dev2-app.ezclocker.com') ||
            url.startsWith('https://dev2-api.ezclocker.com')) {
            return 'dev';
        }

        if (url.startsWith('https://qal.ezclocker.com') ||
            url.startsWith('https://qal-app.ezclocker.com') ||
            url.startsWith('https://qal-api.ezclocker.com')) {
            return 'qal';
        }

        if (url.startsWith('https://e2e.ezclocker.com') ||
            url.startsWith('https://e2e-app.ezclocker.com') ||
            url.startsWith('https://e2e-api.ezclocker.com')) {
            return 'e2e';
        }

        if (url.startsWith('https://prf.ezclocker.com') ||
            url.startsWith('https://prf-app.ezclocker.com') ||
            url.startsWith('https://prf-api.ezclocker.com')) {
            return 'prf';
        }

        if (url.startsWith('https://stage.ezclocker.com') ||
            url.startsWith('https://stg.ezclocker.com') ||
            url.startsWith('https://stg-app.ezclocker.com') ||
            url.startsWith('https://stg-api.ezclocker.com') ||
            url.startsWith('https://stg-orange.ezclocker.com') ||
            url.startsWith('https://stg-orange-app.ezclocker.com') ||
            url.startsWith('https://stg-orange-api.ezclocker.com') ||
            url.startsWith('https://stg-green.ezclocker.com') ||
            url.startsWith('https://stg-green-app.ezclocker.com') ||
            url.startsWith('https://stg-green-api.ezclocker.com')) {
            return 'stg';
        }

        if (url.startsWith('https://ezclocker.com') ||
            url.startsWith('https://app.ezclocker.com') ||
            url.startsWith('https://api.ezclocker.com') ||
            url.startsWith('https://green.ezclocker.com') ||
            url.startsWith('https://green-app.ezclocker.com') ||
            url.startsWith('https://green-api.ezclocker.com') ||
            url.startsWith('https://orange.ezclocker.com') ||
            url.startsWith('https://orange-app.ezclocker.com') ||
            url.startsWith('https://orange-api.ezclocker.com') ||
            url.startsWith('https://blue.ezclocker.com') ||
            url.startsWith('https://blue-app.ezclocker.com') ||
            url.includes('://eznovatech.com') ||
            url.includes('://ezclockers.com') ||
            url.includes('://zclocker.com') ||
            url.includes('://ezclocker.info') ||
            url.includes('://ezclocker.mobi') ||
            url.includes('://ezclocker.net')) {
            return 'prd';
        }

        // Otherwise, default to 'prd'
        return 'prd';
    }

    /**
     * @public @Method
     * Obtains and caches all the current url params
     * @returns {object}
     * Returns the map of url params
     */
    getUrlParams() {
        if (!EzObject.isValid(EzUrlHelper.ezInstance.currentUrlParams)) {
            EzUrlHelper.ezInstance.currentUrlParams = {};

            const parser = document.createElement('a');

            parser.href = window.location.href;

            const query = parser.search.substring(1);

            const vars = query.split('&');

            for (const param of vars) {
                const pair = param.split('=');

                EzUrlHelper.ezInstance.currentUrlParams[pair[0]] = pair[1];
            }
        }

        return EzUrlHelper.ezInstance.currentUrlParams;
    }

    /**
     * @public @function
     * Reloads the URL params
     */
    ezReloadUrlParams() {
        EzUrl.urlParamCache

        EzUrlHelper.ezInstance.currentUrlParams = {};

        const parser = document.createElement('a');

        parser.href = window.location.href;

        const query = parser.search.substring(1);

        const vars = query.split('&');

        for (const param of vars) {
            const pair = param.split('=');

            EzUrlHelper.ezInstance.currentUrlParams[pair[0]] = pair[1];
        }
    }

    /**
     * @public @Method
     * Replaces all + characters in a URL param to spaces.
     * @param {string} value
     * @returns {string}
     */
    ezPlusToSpace(value) {
        if (!EzString.hasLength(value)) {
            return value;
        }

        return value.replace('+', ' ');
    }

    /**
     * @public @Method
     * @param {string} paramName
     * @returns {string}
     * @deprecated
     * Migrate to: EzUrl.readUrlParam(paramName)
     */
    getUrlParam(paramName) {
        return EzUrl.readUrlParam(paramName);
    }

    /**
     * @public @Method
     * @param {string} paramName
     * @returns {string}
     * @deprecated
     * Migrate to: EzUrl.readUrlParam(paramName)
     */
    ezGetUrlParam(paramName) {
        return EzUrl.readUrlParam(paramName);
    }

    /**
     * @public @Method
     * @param {string} paramName
     * @returns {string}
     * @deprecated
     * Migreate to: EzUrl.readUrlParamOrDefault(paramName, defaultValue)
     */
    getUrlParamOrDefault(paramName, defaultValue) {
        return EzUrl.readUrlParamOrDefault(paramName, defaultValue);
    }

    /**
     * @public @Method
     * @param {string} paramName
     * @returns {string}
     * @deprecated
     * Migreate to: EzUrl.readUrlParam(paramName, false)
     */
    getUrlParamNoDecode(paramName) {
        if (!EzString.hasLength(paramName)) {
            return EzString.EMPTY;
        }

        EzUrlHelper.ezInstance.getUrlParams();

        return EzObject.isValid(EzUrlHelper.ezInstance.currentUrlParams[paramName])
            ? EzUrlHelper.ezInstance.currentUrlParams[paramName]
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Returns if debug mode is enabled
     * @returns {boolean}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezUrlHelper.ezDebugMode property
     */
    isDebugMode() {
        return EzUrlHelper.ezInstance.ezDebugMode;
    }

    /**
     * @public @readonly @property
     * Returns true if the employer dashboard page's url has query param 'show-ids=true' OR
     * the hostname is one of the following:
     * @type {boolean}
     */
    get ezShowIds() {
        return 'true' === EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.SHOW_IDS) || EzUrlHelper.ezInstance.ezDebugMode;
    }


    /**
     * @public @method
     * @param {boolean|null} force
     * @returns {string}
     */
    getUtmSourceParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.utmSourceParam)) {
            EzUrlHelper.ezInstance.utmSourceParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.UTM_SOURCE);
        }

        return EzUrlHelper.ezInstance.utmSourceParam;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     * @returns {string}
     */
    getUtmCampaignParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.utmCampaignParam)) {
            EzUrlHelper.ezInstance.utmCampaignParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.UTM_CAMPAIGN);
        }

        return EzUrlHelper.ezInstance.utmCampaignParam;
    }

    /**
     * @public
     * @param {boolean|null} force
     */
    getUtmMedium(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.utmMediumParam)) {
            EzUrlHelper.ezInstance.utmMediumParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.UTM_MEDIUM);
        }

        return EzUrlHelper.ezInstance.utmMediumParam;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getPasswordResetTokenParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.passwordResetToken)) {
            EzUrlHelper.ezInstance.passwordResetToken = EzUrlHelper.ezInstance.getUrlParamNoDecode(EzParamName.TOKEN);
        }

        return EzUrlHelper.ezInstance.passwordResetToken;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getEmployerIdParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.employerIdParam)) {
            EzUrlHelper.ezInstance.employerIdParam = EzUrlHelper.ezInstance.getUrlParamNoDecode(EzParamName.EMPLOYER);
        }

        return EzUrlHelper.ezInstance.employerIdParam;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getErrorParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.errorParam)) {
            EzUrlHelper.ezInstance.errorParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.ERROR);
        }

        return EzUrlHelper.ezInstance.errorParam;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     * @returns {string}
     */
    getEmailParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.emailParam)) {
            EzUrlHelper.ezInstance.emailParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.EMAIL);
        }

        return EzUrlHelper.ezInstance.emailParam;
    }

    /**
     * @public @method
     * Gets the value of the i (industry) url parameter.
     * @param {undefined|null|force} force
     * Default: false
     */
    getIndustryParam(force = false) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.emailParam)) {
            EzUrlHelper.ezInstance.industryParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.INDUSTRY);
        }

        return EzUrlHelper.ezInstance.industryParam;
    }

    /**
     * @public @method
     * Returns the value of url param: v
     * @param {boolean|null} force
     * @returns {string}
     */
    getVersionParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.versionParam)) {
            EzUrlHelper.ezInstance.versionParam = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.REVISION);
        }

        return EzUrlHelper.ezInstance.versionParam;
    }

    /**
     * @public @method
     * Gets the user name query parameter from the document url.
     * @param {boolean|null} force
     */
    getUserNameParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.userName)) {
            EzUrlHelper.ezInstance.userName = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.USER_NAME);
        }

        return EzUrlHelper.ezInstance.userName;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getNameParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.name)) {
            EzUrlHelper.ezInstance.name = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.NAME);

            if (EzUrlHelper.ezInstance.name) {
                EzUrlHelper.ezInstance.name = EzUrlHelper.ezInstance.name.replace('+', ' ');
            }
        }

        return EzUrlHelper.ezInstance.name;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getEmployerNameParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.employerName)) {
            EzUrlHelper.ezInstance.employerName = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.EMPLOYER_NAME);

            if (EzUrlHelper.ezInstance.name) {
                EzUrlHelper.ezInstance.name = EzUrlHelper.ezInstance.name.replace('+', ' ');
            }
        }

        return EzUrlHelper.ezInstance.employerName;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getSourceParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.source)) {
            EzUrlHelper.ezInstance.source = EzUrlHelper.ezInstance.ezGetUrlParam(EzParamName.SOURCE);
        }

        return EzUrlHelper.ezInstance.source;
    }

    /**
     * @public @method
     * @param {boolean|null} force
     */
    getInviteTokenParam(force) {
        if (EzBoolean.isTrue(force) || !EzString.hasLength(EzUrlHelper.ezInstance.inviteToken)) {
            EzUrlHelper.ezInstance.inviteToken = EzUrlHelper.ezInstance.getUrlParamNoDecode(EzParamName.INVITE_TOKEN);
        }

        return EzUrlHelper.ezInstance.inviteToken;
    }

    /**
     * @public @method
     * @param {string} url
     * @param {string} paramName
     * @param {string} paramValue
     */
    appendParam(url, paramName, paramValue) {
        if (!EzString.hasLength(url) || !EzString.hasLength(paramName) || !EzString.hasLength(paramValue)) {
            return url;
        }

        return -1 !== url.indexOf('?')
            ? `${url}&${paramName}=${paramValue}`
            : `${url}?${paramName}=${paramValue}`;
    }

    /**
     * @public @method
     * @param {string} url
     * @param {string} varValue
     */
    addPathVariable(url, varValue) {
        if (!EzString.hasLength(url)) {
            return url;
        }

        if ('/' === url[url.length - 1]) {
            url = `${url}${varValue}`;
        } else {
            url = `${url}/${varValue}`;
        }

        return url;
    }

    /**
     * @public @method
     * @param {string} url
     * @param {*} other
     */
    navigateWithGooleParams(url, other) {
        if (!EzString.hasLength(url)) {
            return;
        }

        let newUrl = EzUrlHelper.ezInstance.appendParam(url, EzUrlHelper.ezInstance.getUtmSourceParam());

        newUrl = EzUrlHelper.ezInstance.appendParam(newUrl, EzUrlHelper.ezInstance.getUtmCampaignParam());

        newUrl = EzUrlHelper.ezInstance.appendParam(newUrl, EzUrlHelper.ezInstance.getUtmMedium());

        if (!EzString.hasLength(other)) {
            newUrl = EzUrlHelper.ezInstance.appendParam(newUrl, other);
        }

        window.location = newUrl;
    }

    /**
     * @public @method
     * Returns the window.location.pathname value if available. Otherwise, returns empty string.
     * @returns {string}
     */
    ezGetWindowLocationPathName() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.pathname)
            ? window.location.pathname
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Returns the window.location.protocol value if available. Otherwise, an empty string.
     * @returns {string}
     */
    ezGetWindowLocationProtocol() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.protocol)
            ? window.location.protocol
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Returns the window.location.host value if available. Otherwise, an empty string.
     * @returns {string}
     */
    ezGetWindowLocationHost() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.host)
            ? window.location.host
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Returns the window.location.search value if available. Otherwise, an empty string.
     * @returns {string}
     */
    ezGetWindowLocationSearch() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.search)
            ? window.location.search
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Builds and returns the full page url from window.location properties.
     * @returns {string}
     */
    ezGetCurrentWindowLocationUrl() {
        return EzUrl.build`
            ${EzUrlHelper.ezInstance.ezGetWindowLocationProtocol()}/
            ${EzUrlHelper.ezInstance.ezGetWindowLocationHost()}/
            ${EzUrlHelper.ezInstance.ezGetWindowLocationPathName()}
            ${EzUrlHelper.ezInstance.ezGetWindowLocationSearch()}`;
    }

    /**
     * @public @method
     * Uses the new URL object to return parts.
     * If the URL object is not available, the urlString is returned.
     * If the urlString is null or empty, empty string is returned.
     * @param {string} urlPartName
     * Must be one of: protocol, host, hostname, port, pathname, search, searchParams, or username. Any other value
     * will result in an undefined response.
     * @param {string} urlString
     * @returns {string}
     */
    ezGetUrlPart(urlPartName, urlString) {
        if (!EzString.hasLength(urlPartName)) {
            throw new EzBadParamException(
                'urlPartName',
                EzUrlHelper.ezInstanc,
                EzUrlHelper.ezInstance.ezGetUrlPart);
        }

        if (!EzString.hasLength(urlString)) {
            throw new EzBadParamException(
                'urlString',
                EzUrlHelper.ezInstanc,
                EzUrlHelper.ezInstance.ezGetUrlPart);
        }

        urlPartName = urlPartName.toLowerCase();

        const result = new URL(urlString)[urlPartName];

        return EzString.hasLength(result)
            ? result
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Uses a <a> tag to parse the provided url and return the specified part.
     * If the URL object is not available, the urlString is returned.
     * If the urlString is null or empty, empty string is returned.
     * @param {string} urlPartName
     * Must be one of: protocol, host, hostname, port, pathname, search, searchParams, or username. Any other value
     * will result in an undefined response.
     * @param {string} urlString
     * @returns {string}
     */
    ezLegacyGetUrlPart(urlPartName, urlString) {
        if (!EzString.hasLength(urlPartName)) {
            throw new EzBadParamException(
                'urlPartName',
                EzUrlHelper.ezInstance,
                EzUrlHelper.ezInstance.ezGetUrlPart);
        }
        if (!EzString.hasLength(urlString)) {
            throw new EzBadParamException(
                'urlString',
                EzUrlHelper.ezInstance,
                EzUrlHelper.ezInstance.ezGetUrlPart);
        }
        if (!EzString.hasLength(urlPartName)) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    The urlPartName must be one of:
                    protocol, host, hostname, port, pathname, search, searchParams, username`);

            return urlString;
        }

        const parser = document.createElement('a');

        parser.href = urlString;

        const regex = /^\?/;

        const queries = parser.search.replace(regex, EzString.EMPTY).split('&');

        const searchObject = [];

        urlPartName = urlPartName.toLowerCase();

        switch (urlPartName) {
            case 'protocol':
                return parser.protocol;
            case 'host':
                return parser.host;
            case 'port':
                return parser.port;
            case 'hostname':
                return parser.hostname;
            case 'pathname':
                return parser.pathname;
            case 'search':
                return parser.search;
            case 'searchParams':
                for (const query of queries) {
                    const split = query.split('=');

                    searchObject[split[0]] = split[1];
                }

                return searchObject;
            default:
                globalThis.ezApi.ezclocker.ezLogger.error(
                    EzString.em`
                        A urlPartName of ${urlPartName}
                        is not supported. Returning the full url as a fall back.`);

                return urlString;
        }
    }

    /**
     * @public @method
     * Determines if the current window.location url has the /secure path.
     * @returns {boolean}
     */
    ezIsLocationUrlEzClockerSecure() {
        const pathName = EzUrlHelper.ezInstance.ezGetWindowLocationPathName();

        if (!EzString.hasLength(pathName)) {
            return false;
        }

        const securePath = '/secure';

        return pathName.length >= securePath.length && securePath === pathName?.substring(0, securePath.length);
    }

    /**
     * @public @method
     * Determines if the current window.location url has the /public path.
     * @returns {boolean}
     */
    ezIsLocationUrlEzClockerPublic() {
        const pathName = EzUrlHelper.ezInstance.ezGetWindowLocationPathName();

        if (!EzString.hasLength(pathName)) {
            return false;
        }

        const publicPath = '/public';

        return pathName.length >= publicPath.length && publicPath === pathName?.substring(0, publicPath.length);
    }

    /**
     * @public @method
     * Replaces the url param (paramName) in the provided url with paramName=paramValue
     *
     * @param {string} url
     * @param {string} paramName
     * @param {string|number} paramValue
     *
     * @returns {string}
     */
    ezReplaceUrlParam(url, paramName, paramValue) {
        if (!EzString.hasLength(paramName)) {
            throw new EzBadParamException(
                'paramName',
                EzVersionChecker,
                EzVersionChecker.ezReplaceUrlParam);
        }
        if (!EzString.hasLength(url)) {
            return `?${paramName}=${EzString.stringOrEmpty(paramValue)}`;
        }

        const pattern = new RegExp(`([?&]${paramName}=).*(\\?|&|#|)`);

        if (-1 < url.search(pattern)) {
            return url.replace(
                pattern,
                `$1${EzString.stringOrEmpty(paramValue)}$2`);
        }

        // Appending new param
        const paramDelimiter = -1 === url.indexOf('?')
            ? '?'
            : '&';

        return `${url}${paramDelimiter}${paramName}=${EzString.stringOrEmpty(paramValue)}`;
    }

    /**
     * @public @method
     * Removes the provided parameter from the provided url.
     * @param {string} url
     * @param {string} parameter
     * @returns {string}
     */
    ezRemoveUrlParameter(url, parameter) {
        if (!EzString.hasLength(url) || !EzString.hasLength(parameter)) {
            return url;
        }

        const urlParts = url.split('?');

        if (2 <= urlParts.length) {
            // Get first part, and remove from array
            const urlBase = urlParts.shift();

            // Join it back up
            const queryString = urlParts.join('?');

            const prefix = `${EzUrl.encodeUrl(parameter)}=`;

            const parts = queryString.split(/[&;]/g);

            // Reverse iteration as may be destructive
            for (let i = parts.length;i > 0;i--) {
                // Idiom for string.startsWith
                if (-1 !== parts[i].lastIndexOf(prefix, 0)) {
                    parts.splice(i, 1);
                }
            }

            url = `${urlBase}?${parts.join('&')}`;
        }

        return url.endsWith('?')
            ? url.substring(0, url.length - 1)
            : url;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezUrlHelper';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzUrlHelper_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzUrlHelper}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzUrlHelper.ezApiName]
        ? globalThis.ezApi.ezclocker[EzUrlHelper.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzUrlHelper}
     */
    static get ezInstance() {
        return EzUrlHelper.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzUrlHelper} instance
     */
    static set ezInstance(instance) {
        if (null != EzUrlHelper.#ezInstance) {
            throw new Error('EzUrlHelper\'s singleton instance is already reigstered with EzApi.');
        }

        EzUrlHelper.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzUrlHelper.REGISTERED
     * @type {string}
     * A valid enum property value from EzUrlHelper
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzUrlHelper.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzUrlHelper
     */
    static get ezApiRegistrationState() {
        return EzUrlHelper.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzUrlHelper
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzUrlHelper.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzUrlHelper.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzUrlHelper.ezInstance && EzRegistrationState.REGISTERED === EzUrlHelper.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzUrlHelper.#ezCanRegister && !EzUrlHelper.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzUrlHelper, EzUrlHelper.ezApiName);
        }

        return EzUrlHelper.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzUrlHelper.ezApiName
     *     2) Property getter EzUrlHelper.ezEventNames
     *     3) Property getter EzUrlHelper.ezInstance
     *     4) Property setter EzUrlHelper.ezInstance
     *     5) Property getter EzUrlHelper.ezApiRegistrationState
     *     6) Property setter EzUrlHelper.ezApiRegistrationState
     *     7) Property getter EzUrlHelper.#ezCanRegister
     *     8) Property getter EzUrlHelper.#ezIsRegistered
     *     9) Method EzUrlHelper.#ezRegistrator()
     */
    static {
        if (!EzUrlHelper.#ezIsRegistered) {
            EzUrlHelper.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzUrlHelper.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzUrlHelper.ezOnEzApiReadyEventName,
                    EzUrlHelper.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
